import {Connection, Edge} from 'reactflow';

import {ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {
  ConditionGroup,
  getMethodIdsImplementingConditionGroup,
} from 'studies/typed/shared/useExperimentDesignState';

import {getProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';

/**
 * A grid node's role in the upstream->active draw affordance: `source` for nodes in the layer
 * immediately upstream of the active method, `target` for nodes in the active layer (only when an
 * upstream layer exists to draw from), `null` for everything else (and for every node when there is
 * no active layer, or when the active layer is the first column so nothing is upstream).
 */
export type ConnectionRole = 'source' | 'target' | null;

/** Minimal directed-edge shape (endpoints by node id). Live ReactFlow `Edge`s satisfy this too. */
interface EdgeEndpoints {
  source: string;
  target: string;
}

/**
 * Derives connection data for the upstream->active draw affordance. The role map is static (depends
 * only on the methods and the active layer). The source-handle visibility map additionally depends
 * on the live edges. Both maps are empty when there is no active layer.
 */
export function getNodeConnectionRoles(
  conditions: Array<ConditionGroup>,
  methods: Array<ExperimentMethod>,
  edges: Array<EdgeEndpoints>,
  activeMethodId: string | null
): {
  roleByNodeId: ReadonlyMap<string, ConnectionRole>;
  showPersistentSourceHandleByNodeId: ReadonlyMap<string, boolean>;
} {
  const {roleByNodeId, activeNodeIds, sourceNodeIds} = buildRoleByNodeId(conditions, methods, activeMethodId);
  const showPersistentSourceHandleByNodeId = buildShowPersistentSourceHandleByNodeId(
    edges,
    activeNodeIds,
    sourceNodeIds
  );
  return {roleByNodeId, showPersistentSourceHandleByNodeId};
}

function getConnectionRole(
  methodId: string,
  activeMethodId: string | null,
  upstreamMethodId: string | null
): ConnectionRole {
  // With no upstream layer there is nothing to draw an incoming edge from, so the active layer
  // gets no `target` role (and no source nodes exist either) — every node is `null`.
  if (methodId === activeMethodId && upstreamMethodId != null) {
    return 'target';
  } else if (methodId === upstreamMethodId) {
    return 'source';
  } else {
    return null;
  }
}

/**
 * Assigns each grid node's connection role: active-layer nodes are `target` when an upstream layer
 * exists, the single immediately-upstream layer's nodes are `source`, all others `null`. When the
 * active layer is the first column there is no upstream layer to connect from, so its nodes are
 * `null` too. Also returns the active/source id sets the edge-dependent pass needs. Empty when there
 * is no active layer.
 */
function buildRoleByNodeId(
  conditions: Array<ConditionGroup>,
  methods: Array<ExperimentMethod>,
  activeMethodId: string | null
): {roleByNodeId: Map<string, ConnectionRole>; activeNodeIds: Set<string>; sourceNodeIds: Set<string>} {
  const roleByNodeId = new Map<string, ConnectionRole>();
  const activeNodeIds = new Set<string>();
  const sourceNodeIds = new Set<string>();
  const activeIndex =
    activeMethodId == null ? -1 : methods.findIndex((method) => method.id === activeMethodId);
  if (activeIndex < 0) {
    return {roleByNodeId, activeNodeIds, sourceNodeIds};
  }
  // The single immediately-preceding column, undefined when the active layer is the first column.
  const hasUpstreamLayer = activeIndex > 0;
  const upstreamMethodId = hasUpstreamLayer ? methods[activeIndex - 1].id : null;
  conditions.forEach((condition, conditionIndex) => {
    for (let replicateIndex = 0; replicateIndex < condition.numReplicates; replicateIndex++) {
      methods.forEach((method) => {
        const nodeId = getProcedureRunDesignFlowchartNodeId({
          conditionIndex,
          replicateIndex,
          methodId: method.id,
        });
        const role: ConnectionRole = getConnectionRole(method.id, activeMethodId, upstreamMethodId);
        roleByNodeId.set(nodeId, role);
        if (role === 'target') {
          activeNodeIds.add(nodeId);
        } else if (role === 'source') {
          sourceNodeIds.add(nodeId);
        }
      });
    }
  });
  return {roleByNodeId, activeNodeIds, sourceNodeIds};
}

/**
 * Edge-dependent visibility of each upstream source handle: while any active-layer node still lacks
 * an incoming upstream->active edge, every source handle shows. Once every active node has one, only
 * sources without an outgoing upstream->active edge keep showing. Edges other than upstream->active
 * are ignored.
 */
function buildShowPersistentSourceHandleByNodeId(
  edges: Array<EdgeEndpoints>,
  activeNodeIds: Set<string>,
  sourceNodeIds: Set<string>
): Map<string, boolean> {
  const relevant = edges.filter((edge) => sourceNodeIds.has(edge.source) && activeNodeIds.has(edge.target));
  const incoming = new Set(relevant.map((edge) => edge.target));
  const outgoing = new Set(relevant.map((edge) => edge.source));
  const anyActiveLacksIncoming = activeNodeIds.size > 0 && [...activeNodeIds].some((id) => !incoming.has(id));
  return new Map([...sourceNodeIds].map((id) => [id, anyActiveLacksIncoming || !outgoing.has(id)]));
}

/**
 * Whether a drag-to-connect attempt is a legal upstream->active connection: a brand-new edge from any
 * upstream source node to an active-layer target node. Self-connections and duplicates are rejected.
 * Source-handle persistence/visibility does not gate drawing, so split/pool edges can still be drawn
 * into already-connected active nodes. Roles are looked up by node id via {@link getConnectionRoleByNodeId},
 * never parsed out of ids, so this stays in lockstep with the derived roles.
 */
export function isUpstreamToActiveConnection(
  connection: Connection,
  existingEdges: Array<Edge>,
  getConnectionRoleByNodeId: (nodeId: string) => ConnectionRole
): boolean {
  const {source, target} = connection;
  if (source == null || target == null || source === target) {
    return false;
  }
  const isDuplicate = existingEdges.some((edge) => edge.source === source && edge.target === target);
  if (isDuplicate) {
    return false;
  }
  return getConnectionRoleByNodeId(source) === 'source' && getConnectionRoleByNodeId(target) === 'target';
}

/**
 * True when every rendered active-layer node has an incoming edge from the immediately upstream method
 * column. FE Save-gate mirror of the backend's reachability validation: only
 * upstream->active edges are editable and the rest of the loaded graph is valid, so active-layer
 * connectivity implies full reachability. Returns true when there is no active layer or it is the first
 * column. The backend remains authoritative.
 */
export function isActiveLayerConnected(
  conditions: Array<ConditionGroup>,
  methods: Array<ExperimentMethod>,
  edges: Array<EdgeEndpoints>,
  activeMethodId: string | null
): boolean {
  const activeIndex =
    activeMethodId == null ? -1 : methods.findIndex((method) => method.id === activeMethodId);
  if (activeIndex <= 0) {
    // No active layer, or the active layer is the first column: nothing can be disconnected.
    return true;
  }
  const activeMethod = methods[activeIndex];
  const upstreamMethodId = methods[activeIndex - 1].id;
  // Only include nodes that actually render (per condition coverage), matching the displayed grid.
  const activeNodeIds = new Set<string>();
  const upstreamNodeIds = new Set<string>();
  conditions.forEach((condition, conditionIndex) => {
    const covered =
      getMethodIdsImplementingConditionGroup(condition) ?? new Set(methods.map((method) => method.id));
    for (let replicateIndex = 0; replicateIndex < condition.numReplicates; replicateIndex++) {
      if (covered.has(activeMethod.id)) {
        activeNodeIds.add(
          getProcedureRunDesignFlowchartNodeId({conditionIndex, replicateIndex, methodId: activeMethod.id})
        );
      }
      if (covered.has(upstreamMethodId)) {
        upstreamNodeIds.add(
          getProcedureRunDesignFlowchartNodeId({conditionIndex, replicateIndex, methodId: upstreamMethodId})
        );
      }
    }
  });
  const incoming = new Set(
    edges
      .filter((edge) => upstreamNodeIds.has(edge.source) && activeNodeIds.has(edge.target))
      .map((edge) => edge.target)
  );
  return [...activeNodeIds].every((id) => incoming.has(id));
}
